import { useEffect, type ReactNode } from 'react'

/**
 * El panel lateral donde ocurren las acciones sobre una cita.
 *
 * Lateral y no modal centrado, por una razón concreta: **la agenda tiene que
 * seguir a la vista**. Quien está agendando mira los huecos de al lado para
 * decidir, y un modal que tapa la grilla obliga a cerrarlo, mirar, y volver a
 * abrirlo.
 *
 * Cierra con Escape y con el fondo, que es lo que la mano espera.
 */
export function Panel({
  titulo,
  bajada,
  onCerrar,
  children,
  pie,
}: {
  titulo: string
  bajada?: string
  onCerrar: () => void
  children: ReactNode
  pie?: ReactNode
}) {
  useEffect(() => {
    const alTecla = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onCerrar()
    }
    window.addEventListener('keydown', alTecla)
    return () => window.removeEventListener('keydown', alTecla)
  }, [onCerrar])

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-slate-900/20"
        onClick={onCerrar}
        aria-hidden="true"
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className="relative flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-xl"
      >
        <header className="flex items-start gap-3 border-b border-slate-200 px-6 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="txt-seccion truncate">{titulo}</h2>
            {bajada && <p className="txt-secundario mt-0.5">{bajada}</p>}
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="-mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            ×
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">{children}</div>

        {pie && <footer className="border-t border-slate-200 px-6 py-4">{pie}</footer>}
      </aside>
    </div>
  )
}
